/**
 * Arquitectura Page - Editorial Style
 * Complete process/methodology showcase
 */

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { PageWrapper } from '../components/EditorialShared';

// Timeline stages
const stages = [
  {
    id: 'diagnostico',
    number: '01',
    title: 'Diagnóstico Estratégico',
    description: 'Analizamos tu marca, tu mercado y tus objetivos. Entendemos dónde estás y hacia dónde quieres llegar.',
    features: [
      'Análisis de posicionamiento actual',
      'Estudio de competencia visual',
      'Definición de objetivos claros',
      'Identificación de oportunidades'
    ],
    cta: {
      text: 'Ver ejemplo',
      href: 'https://drive.google.com/file/d/1YcksQN3f9fhHQbQzheGEuVzoW_-Csfc3/view'
    }
  },
  {
    id: 'moodboard',
    number: '02',
    title: 'Moodboard Estratégico',
    description: 'Antes de producir, construimos una dirección visual clara.',
    features: [
      'Paleta de colores definida',
      'Referencias visuales curadas',
      'Estilo fotográfico específico',
      'Narrativa visual coherente'
    ],
    cta: {
      text: 'Ver ejemplo',
      href: 'https://drive.google.com/file/d/14CBHc37Z3w0VaMHEcjUX_Jdb52dxN1KD/view'
    }
  },
  {
    id: 'planeacion',
    number: '03',
    title: 'Planeación de Producción',
    description: 'Cada sesión tiene estructura. Guiones. Objetivos. Lista de tomas.',
    features: [
      'Guión detallado de sesión',
      'Lista de tomas específicas',
      'Cronograma de producción',
      'Checklist de materiales'
    ],
    cta: {
      text: 'Ver ejemplo',
      href: 'https://drive.google.com/file/d/1CfH_xahHiRCXmxlREcYxDm8J6IjCr4IG/view'
    }
  },
  {
    id: 'produccion',
    number: '04',
    title: 'Producción',
    description: 'No improvisamos. Capturamos con intención estratégica.',
    features: [
      'Dirección creativa profesional',
      'Equipo especializado',
      'Iluminación estratégica',
      'Captura con intención de marca'
    ],
    cta: {
      text: 'Agendar sesión',
      href: 'https://wa.me/527774385617',
      isPrimary: true
    }
  },
  {
    id: 'entrega',
    number: '05',
    title: 'Entrega Profesional',
    description: 'El cliente recibe un sistema completo, no solo archivos sueltos.',
    features: [
      'PDF de resultados y métricas',
      'Material organizado',
      'Look & feel estructurado',
      'Planeación mensual de contenido'
    ],
    cta: {
      text: 'Ver ejemplo',
      href: 'https://drive.google.com/file/d/1f8txaA_KvAQWqP8Mlpa-Be-sDMgpjIb7/view'
    }
  }
];

// Stage Card
const StageCard = ({ stage, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      className="grid md:grid-cols-12 gap-8 md:gap-12 py-16 md:py-20 border-t border-white/10"
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      {/* Number */}
      <div className="md:col-span-2">
        <span className="text-white/20 text-6xl md:text-8xl font-extralight">
          {stage.number}
        </span>
      </div>

      {/* Content */}
      <div className="md:col-span-5">
        <h2 className="text-white text-3xl md:text-4xl font-extralight mb-4">
          {stage.title}
        </h2>
        <p className="text-white/50 text-lg font-light">
          {stage.description}
        </p>
      </div>

      {/* Features */}
      <div className="md:col-span-5">
        <ul className="space-y-3 mb-8">
          {stage.features.map((feature, i) => (
            <li key={i} className="text-white/60 flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-white/40 rounded-full mt-2 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        {stage.cta && (
          <a
            href={stage.cta.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-6 py-3 transition-colors ${
              stage.cta.isPrimary
                ? 'bg-white text-black hover:bg-white/90'
                : 'border border-white/20 text-white hover:bg-white/5'
            }`}
            data-cursor="ver"
          >
            {stage.cta.text}
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </motion.div>
  );
};

// Main Page
export function ArquitecturaPage() {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });

  return (
    <PageWrapper showLogoFooter={true}>
      {/* Hero */}
      <section className="min-h-[80vh] flex items-center pt-20" ref={heroRef}>
        <div className="max-w-5xl mx-auto px-6">
          <motion.span
            className="text-white/40 font-medium text-sm tracking-widest uppercase mb-6 block"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            Sistema Propietario
          </motion.span>
          
          <motion.h1
            className="text-white text-5xl md:text-7xl lg:text-8xl font-extralight leading-[0.95] mb-8"
            initial={{ opacity: 0, y: 60 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.5 }}
          >
            Arquitectura
            <br />
            Paliacate
          </motion.h1>

          <motion.p
            className="text-white/50 text-xl md:text-2xl font-light max-w-2xl"
            initial={{ opacity: 0, y: 40 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8 }}
          >
            No improvisamos contenido. Trabajamos bajo un sistema estructurado 
            y profesional que transforma marcas en referentes visuales.
          </motion.p>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="max-w-6xl mx-auto px-6">
        {stages.map((stage, index) => (
          <StageCard key={stage.id} stage={stage} index={index} />
        ))}
      </section>

      {/* Final CTA */}
      <section className="py-32 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h2
            className="text-white text-4xl md:text-6xl font-extralight mb-8"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            ¿Listo para elevar
            <br />
            tu marca?
          </motion.h2>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
          >
            <a
              href="https://wa.me/527774385617"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors"
              data-cursor="agendar"
            >
              Agendar Diagnóstico
            </a>
            <Link
              to="/metodo"
              className="px-8 py-4 border border-white/20 text-white hover:bg-white/5 transition-colors"
              data-cursor="ver"
            >
              Conocer nuestro método
            </Link>
          </motion.div>
        </div>
      </section>
    </PageWrapper>
  );
}

export default ArquitecturaPage;
